/*
 * Copyright (C) 2019 - Potentially Ltd
 *
 * Please see distribution for license.
 */

import { Location } from '@angular/common';
import { TranslocoService } from '@ngneat/transloco';
import { catchError, switchMap } from 'rxjs/operators';
import { environment } from '../../../../../../environments/environment';
import {
  BrowseSettings,
  BrowseSettingsRequest,
  DisplayConfigRequest,
  OrganizationImageUpdateRequest,
  OrganizationNameUpdateRequest,
} from '../../../../../shared/models';
import { ItemWithUploadUrl } from '../../../../../shared/models/ItemWithUploadUrl';
import { RestClientService } from '../../../../../shared/services/rest-client.service';
import { ObservableResult } from '../../../../../shared/store';
import { PlaylistAuthorDataService } from './data.service';

export class PlaylistApiAuthorDataService implements PlaylistAuthorDataService {
  constructor(
    private client: RestClientService,
    private translocoService: TranslocoService,
  ) {}

  updateVisibility(playlistUid: string, request: DisplayConfigRequest): ObservableResult<void> {
    return this.client
      .post<void>(Location.joinWithSlash(environment.apiRootUrl || '', `/playlists/${playlistUid}/author/config`), request)
      .pipe(
        switchMap(({ body }) => ObservableResult.ofSuccess(body)),
        catchError(() => ObservableResult.ofError(this.translocoService.translate('translations.errors.errorUpdateAuthorVisibility'))),
      );
  }

  updateOrganizationImage(playlistUid: string, request: OrganizationImageUpdateRequest): ObservableResult<ItemWithUploadUrl<void>> {
    return this.client
      .post<
        ItemWithUploadUrl<void>
      >(Location.joinWithSlash(environment.apiRootUrl || '', `/playlists/${playlistUid}/author/external/image`), request)
      .pipe(
        switchMap(({ body }) => ObservableResult.ofSuccess(body)),
        catchError(() => ObservableResult.ofError(this.translocoService.translate('translations.errors.errorUpdateOrganizationImage'))),
      );
  }

  updateOrganizationName(playlistUid: string, request: OrganizationNameUpdateRequest): ObservableResult<void> {
    return this.client
      .post<void>(Location.joinWithSlash(environment.apiRootUrl || '', `/playlists/${playlistUid}/author/external/name`), request)
      .pipe(
        switchMap(({ body }) => ObservableResult.ofSuccess(body)),
        catchError(() => ObservableResult.ofError(this.translocoService.translate('translations.errors.errorUpdateOrganizationName'))),
      );
  }

  setIsShared(playlistUid: string, allow: boolean): ObservableResult<boolean> {
    return this.client
      .put<boolean>(Location.joinWithSlash(environment.apiRootUrl || '', `/playlists/${playlistUid}/target/is-shared`), null, {
        allow: allow,
      })
      .pipe(
        switchMap(({ body }) => ObservableResult.ofSuccess(body)),
        catchError(() => ObservableResult.ofError(this.translocoService.translate('translations.errors.finalizeError'))),
      );
  }

  updateBrowseSettings(playlistUid: string, request: BrowseSettingsRequest): ObservableResult<BrowseSettings> {
    return this.client
      .put<BrowseSettings>(Location.joinWithSlash(environment.apiRootUrl || '', `/playlists/${playlistUid}/browse-settings`), request)
      .pipe(
        switchMap(({ body }) => ObservableResult.ofSuccess(body)),
        catchError(() =>
          ObservableResult.ofError(this.translocoService.translate('translations.programs.publishSettings.browse.updateFailed')),
        ),
      );
  }
}
